"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { MentorPreview } from "@/types/mentor";

export function MentorCard({ mentor }: { mentor: MentorPreview }) {
  const t = useTranslations("mentorCard");
  const imageId = mentor.profile_photo?.id;
  const imageUrl = imageId ? `/api/assets/${imageId}` : null;

  // Highlighted skills first, then the rest — show up to 6
  const skills = [...(mentor.mentor_skills ?? [])]
    .sort((a, b) => (b.highlight ? 1 : 0) - (a.highlight ? 1 : 0))
    .slice(0, 6);

  const highlightedCount = skills.filter((s) => s.highlight).length;

  return (
    <Link
      href={`/mentors/${mentor.slug}`}
      className="group block w-full max-w-[520px] mx-auto min-h-[240px] rounded-2xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
        borderTopColor: "#f87171",
        borderTopWidth: "3px",
      }}
    >
      <div className="grid grid-cols-[128px_1fr] sm:grid-cols-[170px_1fr] items-start h-full">
        {/* Portrait */}
        <div
          className="relative h-full min-h-[240px] overflow-hidden shrink-0"
          style={{
            background: "var(--color-bg)",
            borderRight: "1px solid var(--color-border)",
          }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={mentor.name}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105"
              sizes="(max-width: 640px) 128px, 170px"
              unoptimized
            />
          ) : (
            <div
              className="flex h-full items-center justify-center text-3xl font-semibold select-none"
              style={{
                background: "var(--color-surface)",
                color: "var(--color-muted)",
              }}
            >
              {mentor.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Details */}
  <div className="min-w-0 text-left p-4 sm:p-5">
          <h3
            className="text-base sm:text-lg font-bold leading-snug"
            style={{ color: "var(--color-text)" }}
          >
            {mentor.name}
          </h3>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {mentor.location && (
              <span className="text-xs" style={{ color: "var(--color-muted)" }}>
                {mentor.location}
              </span>
            )}
            {mentor.years_experience != null && (
              <span className="text-xs font-medium" style={{ color: "var(--color-subtle)" }}>
                {t("yrsCoaching", { years: mentor.years_experience })}
              </span>
            )}
          </div>

          {/* Tagline — the mentor's pitch */}
          {mentor.tagline && (
            <p
              className="mt-2 text-xs sm:text-sm line-clamp-2 leading-relaxed"
              style={{ color: "var(--color-muted)", fontStyle: "italic" }}
            >
              {mentor.tagline}
            </p>
          )}

          {/* Skills */}
          {skills.length > 0 && (
            <div className="mt-3">
              <p
                className="mb-2 text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: "var(--color-subtle)" }}
              >
                {highlightedCount > 0 ? t("specialisesIn") : t("skills")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((ms) => (
                  <span
                    key={ms.skill.slug ?? ms.skill.name}
                    className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium"
                    style={
                      ms.highlight
                        ? {
                            background: "var(--color-brand)",
                            borderColor: "var(--color-brand)",
                            color: "#fff",
                          }
                        : {
                            background: "var(--color-bg)",
                            borderColor: "var(--color-border)",
                            color: "var(--color-text)",
                          }
                    }
                  >
                    {ms.skill.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>



    </Link>
  );
}



